<script lang="ts" setup>
  import { useBreakpoints } from '@storefront/storefront';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import type { Paginator } from '@othercommerce/domain';
  import type { ListOrder } from '@storefront/resources';

  interface Props {
    orders: Paginator<ListOrder>;
  }

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  defineProps<Props>();

  const breakpoints = useBreakpoints();
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.ORDERS') }}
  </AccountMobileNavigation>
  <SfTile large v-if="orders.data.length > 0" class="account-orders">
    <template #title v-if="breakpoints.lg">
      <h1 class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.ORDERS') }}
      </h1>
    </template>
    <template #default>
      <div class="account-orders__content">
        <SfListCard v-for="order in orders.data" :key="order.id" class="account-orders">
          <template #icons>
            <SfOrderStatus :status="order.status" />
          </template>
          <template #default>
            <div class="account-orders__header">
              <h2 class="account-orders__heading">
                {{ $t('SHOP.ACCOUNT.HEADER.ORDER_LISTING_NUMBER', { number: order.number }) }}
              </h2>
              <p class="account-orders__date">
                <SfDateTime :value="order.orderedAt" />
              </p>
            </div>
            <div class="account-orders__content">
              <span class="account-orders__count">
                {{ $tc('SHOP.ACCOUNT.MESSAGE.ORDER_ITEMS_COUNT', order.itemsCount, { count: order.itemsCount }) }}
              </span>
              <span class="account-orders__price">
                <SfMoney :code="order.currency.code" :decimals="order.currency.decimals" :value="order.totalGross" />
              </span>
            </div>
          </template>
          <template #actions>
            <SfButton :href="$route('web.account.order.show', { order: order.id })" class="account-orders__details">
              {{ $t('SHOP.ACTION.SHOW_DETAILS') }}
            </SfButton>
          </template>
        </SfListCard>
      </div>
    </template>
  </SfTile>

  <SfTile v-else>
    <SfDataPlaceholder>
      {{ $t('SHOP.ACCOUNT.MESSAGE.ORDER_PLACEHOLDER') }}
    </SfDataPlaceholder>
  </SfTile>
  <SfPagination :pagination="orders" />

  <RouterView />
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-orders {

    &__content {
      display: flex;
      flex-direction: column;
      row-gap: 1.25rem;
    }

    &__header {
      display: flex;
      align-items: center;
      column-gap: 0.625rem;
      flex-wrap: wrap;
    }

    &__heading {
      font-size: 1.25rem;
      word-break: break-all;
    }

    &__content {
      display: flex;
      flex-direction: column;
    }

    &__date,
    &__count {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
    }

    &__price {
      font-weight: var(--font-weight-bold);
      margin-top: 0.125rem;
    }

    &__details {
      margin-top: 1rem;
    }
  }

  @include media.query(lg) {
    .account-orders {
      row-gap: 1rem;

      &__header {
        flex-direction: column;
        align-items: flex-start;
      }

      &__heading {
        font-size: 1rem;
      }

      &__content {
        gap: 0.75rem;

        .list-card__actions {
          gap: 0.75rem;
        }
      }

      .list-card__content-default{
        gap: 1rem;
      }

      &__details {
        margin-top: 0;
      }
    }
  }
</style>
